// Records a short voice clip and re-renders it pitch-shifted downward
// into a deep, male-sounding register, so the speaker can't be
// recognized. This uses
// SoundTouchJS (a JS port of the well-established SoundTouch audio
// library) for the actual pitch shifting — its `pitch` control shifts
// frequency while automatically compensating stretch/rate internally so
// the output stays the same length as the original recording.
//
// An earlier version of this file hand-rolled pitch shifting (plain
// playback-rate resampling, then a custom WSOLA time-stretch on top of
// that). Both were buggy in practice — the recording came out sped up,
// then later came out clicky/warbly/echoey once a custom time-stretch
// was layered in. Using a mature, purpose-built library instead of
// custom DSP avoids re-debugging the same class of audio artifacts.
import { SimpleFilter, SoundTouch, WebAudioBufferSource } from 'soundtouchjs';

// A large, uniform pitch drop is what makes a disguised voice sound
// "processed" — the formants (resonances that make a voice sound like
// a real person rather than a slowed-down recording) get compressed
// along with the pitch. Real voices vary in exactly how deep they are,
// so instead of one shift applied the same way every time, each
// recording is disguised with a randomly-picked PRESET: a distinct
// pitch range paired with an effects chain tuned for that character.
// Mirrors the full spread of a typical short-video app's voice-changer
// panel (Trending / Characters / Enhancers) — some presets are subtle,
// natural disguises, others are overtly playful/character voices — so
// clips from the same person don't all carry an identical, recognizable
// "signature," and there's real variety to pick from.
interface FormantBand { freq: number; gain: number; q: number; }

interface VoicePreset {
  name: string;
  // Range SoundTouchJS's `pitch` is randomized within. <1 = deeper,
  // >1 = higher. Values close to 1.0 stay natural; further from 1.0
  // reads as more of a character voice.
  pitchRange: [number, number];
  // Peaking EQ bands approximating formant correction / character
  // tone-shaping. Boosting specific bands (rather than one broad
  // high-shelf) keeps speech intelligible without sounding harsh.
  formantBands?: FormantBand[];
  // Band-limits the signal (e.g. a phone-call or megaphone effect).
  bandpass?: { low: number; high: number };
  // Algorithmic reverb (generated impulse response) — Cave/Church.
  reverb?: { seconds: number; decay: number; mix: number };
  // Feedback delay — Echo/Cave.
  echo?: { delaySeconds: number; feedback: number; mix: number };
  // Waveshaper drive — Megaphone/Devil's Advocate.
  distortion?: { amount: number; mix: number };
  // LFO-modulated micro-delay — Vibrato/Granny/Magnetic/Trickster.
  vibrato?: { rateHz: number; depthMs: number; mix: number };
  // Post-process bit-depth reduction for a lo-fi/crackly character —
  // Phonograph/Synth. Applied directly to samples, not the audio graph.
  bitcrushBits?: number;
}

const VOICE_PRESETS: VoicePreset[] = [
  // --- Natural disguise voices ---
  { name: 'Deep', pitchRange: [0.78, 0.85], formantBands: [
    { freq: 600, gain: 3, q: 1 }, { freq: 1800, gain: 4, q: 1.2 }, { freq: 3000, gain: 3, q: 1 },
  ]},
  { name: 'Baritone', pitchRange: [0.81, 0.88], formantBands: [
    { freq: 500, gain: 2, q: 1 }, { freq: 1600, gain: 3, q: 1.2 }, { freq: 2800, gain: 2.5, q: 1 },
  ]},
  { name: 'Alfred', pitchRange: [0.80, 0.87], formantBands: [
    { freq: 550, gain: 2.5, q: 1 }, { freq: 1500, gain: 2, q: 1.4 },
  ]},
  { name: 'Philosopher', pitchRange: [0.80, 0.88], formantBands: [
    { freq: 500, gain: 3, q: 0.8 }, { freq: 1400, gain: 2, q: 1.2 },
  ], reverb: { seconds: 1.2, decay: 3, mix: 0.08 }},
  { name: 'Literature', pitchRange: [0.81, 0.88], formantBands: [
    { freq: 700, gain: 3, q: 1 }, { freq: 1900, gain: 2, q: 1.1 },
  ]},
  { name: 'Angeleno', pitchRange: [1.13, 1.22], formantBands: [
    { freq: 1000, gain: 2, q: 1 }, { freq: 2400, gain: 2.5, q: 1.2 },
  ]},
  { name: 'Jessie', pitchRange: [1.14, 1.23], formantBands: [
    { freq: 900, gain: 2, q: 1 }, { freq: 2200, gain: 2, q: 1.1 },
  ]},
  { name: 'Marty', pitchRange: [0.79, 0.87], formantBands: [
    { freq: 800, gain: 2, q: 1 }, { freq: 2000, gain: 2, q: 1 },
  ]},
  { name: 'Mr. Meticulous', pitchRange: [1.15, 1.24], formantBands: [
    { freq: 1200, gain: 3, q: 1.3 }, { freq: 3200, gain: 2.5, q: 1 },
  ]},
  { name: 'Explorer', pitchRange: [0.80, 0.88], formantBands: [
    { freq: 1000, gain: 2, q: 1 },
  ], echo: { delaySeconds: 0.09, feedback: 0.12, mix: 0.1 }},
  { name: 'Sneaky', pitchRange: [0.80, 0.88], bandpass: { low: 250, high: 4200 }, formantBands: [
    { freq: 900, gain: 2, q: 1.3 },
  ]},
  { name: 'Varsity', pitchRange: [1.15, 1.25], formantBands: [
    { freq: 1200, gain: 3, q: 1.2 }, { freq: 3000, gain: 2, q: 1 },
  ]},

  // --- Phone / room-tone effects ---
  { name: 'Telephone', pitchRange: [0.80, 0.88], bandpass: { low: 300, high: 3400 }, formantBands: [
    { freq: 1200, gain: 3, q: 1.2 }, { freq: 2200, gain: 3, q: 1.2 },
  ]},
  { name: 'Megaphone', pitchRange: [1.13, 1.22], bandpass: { low: 500, high: 3000 },
    distortion: { amount: 18, mix: 0.35 }, formantBands: [{ freq: 1500, gain: 4, q: 1.3 }]},
  { name: 'Phonograph', pitchRange: [0.80, 0.88], bandpass: { low: 400, high: 3800 },
    bitcrushBits: 8, formantBands: [{ freq: 1500, gain: 3, q: 1.2 }]},
  { name: 'Cave', pitchRange: [0.79, 0.87], reverb: { seconds: 2.2, decay: 4, mix: 0.35 },
    echo: { delaySeconds: 0.18, feedback: 0.3, mix: 0.2 }},
  { name: 'Church', pitchRange: [0.80, 0.88], reverb: { seconds: 3.5, decay: 5, mix: 0.4 }},
  { name: 'Mic', pitchRange: [1.15, 1.24], formantBands: [
    { freq: 3000, gain: 4, q: 1 }, { freq: 6000, gain: 3, q: 0.8 },
  ]},
  { name: 'Equalizer', pitchRange: [0.80, 0.88], formantBands: [
    { freq: 300, gain: -3, q: 1 }, { freq: 1000, gain: 4, q: 1.2 }, { freq: 4000, gain: 4, q: 1 },
  ]},

  // --- Character / novelty voices ---
  { name: 'Teenager', pitchRange: [1.14, 1.23], formantBands: [
    { freq: 1500, gain: 3, q: 1.2 }, { freq: 3200, gain: 2, q: 1 },
  ]},
  { name: 'Chipmunk', pitchRange: [1.35, 1.5], formantBands: [
    { freq: 2000, gain: 3, q: 1 },
  ]},
  { name: 'Helium', pitchRange: [1.45, 1.6], formantBands: [
    { freq: 2200, gain: 3, q: 1 },
  ]},
  { name: 'Giant', pitchRange: [0.55, 0.65], reverb: { seconds: 0.8, decay: 2, mix: 0.15 },
    formantBands: [{ freq: 400, gain: 4, q: 0.9 }]},
  { name: 'Darling', pitchRange: [1.14, 1.24], formantBands: [
    { freq: 1200, gain: 3, q: 1.1 }, { freq: 2800, gain: 3, q: 1 },
  ]},
  { name: 'Beauty Guru', pitchRange: [1.14, 1.23], formantBands: [
    { freq: 1400, gain: 3, q: 1.1 }, { freq: 3000, gain: 3, q: 1 },
  ]},
  { name: 'Granny', pitchRange: [1.16, 1.26], vibrato: { rateHz: 5.5, depthMs: 1.5, mix: 0.5 },
    formantBands: [{ freq: 1500, gain: 2, q: 1.2 }]},
  { name: 'Santa', pitchRange: [0.76, 0.84], reverb: { seconds: 0.6, decay: 2, mix: 0.12 },
    formantBands: [{ freq: 700, gain: 3, q: 1 }]},
  { name: "Devil's Advocate", pitchRange: [0.6, 0.7], distortion: { amount: 12, mix: 0.25 },
    formantBands: [{ freq: 500, gain: 3, q: 0.9 }]},
  { name: 'Sorcerer', pitchRange: [0.73, 0.83], reverb: { seconds: 1.8, decay: 3.5, mix: 0.3 },
    vibrato: { rateHz: 3, depthMs: 2, mix: 0.3 }},
  { name: 'Trickster', pitchRange: [1.14, 1.28], vibrato: { rateHz: 6, depthMs: 2, mix: 0.35 },
    formantBands: [{ freq: 1600, gain: 3, q: 1.1 }]},
  { name: 'Magnetic', pitchRange: [0.80, 0.88], vibrato: { rateHz: 2, depthMs: 3, mix: 0.4 },
    formantBands: [{ freq: 800, gain: 2, q: 1 }]},

  // --- Enhancer FX ---
  { name: 'Echo', pitchRange: [1.15, 1.25], echo: { delaySeconds: 0.22, feedback: 0.35, mix: 0.35 }},
  { name: 'Vibrato', pitchRange: [0.80, 0.88], vibrato: { rateHz: 5, depthMs: 2.5, mix: 0.5 }},
  { name: 'Synth', pitchRange: [1.15, 1.28], bitcrushBits: 6, formantBands: [
    { freq: 1500, gain: 4, q: 2 },
  ]},
];

function randomInRange(min: number, max: number): number {
  return min + Math.random() * (max - min);
}

function pickRandomPreset(): VoicePreset {
  return VOICE_PRESETS[Math.floor(Math.random() * VOICE_PRESETS.length)];
}

// Exposed so the UI can list preset names (e.g. for a manual picker),
// without callers needing to duplicate the list.
export const VOICE_PRESET_NAMES: string[] = VOICE_PRESETS.map((p) => p.name);

// Pulls every processed frame out of a SoundTouchJS filter. The filter
// works like a stream: each call to extract() returns up to `chunkSize`
// frames, and returning fewer than requested means the source has run
// dry, so that's the signal to stop.
function extractProcessedSamples(filter: SimpleFilter, numChannels: number): Float32Array[] {
  const chunkSize = 4096;
  const leftChunks: Float32Array[] = [];
  const rightChunks: Float32Array[] = [];
  const temp = new Float32Array(chunkSize * 2); // interleaved L/R

  while (true) {
    const framesExtracted = filter.extract(temp, chunkSize);
    if (framesExtracted <= 0) break;

    const left = new Float32Array(framesExtracted);
    leftChunks.push(left);
    const right = numChannels > 1 ? new Float32Array(framesExtracted) : null;
    if (right) rightChunks.push(right);

    for (let i = 0; i < framesExtracted; i++) {
      left[i] = temp[i * 2];
      if (right) right[i] = temp[i * 2 + 1];
    }

    if (framesExtracted < chunkSize) break;
  }

  const concat = (chunks: Float32Array[]): Float32Array => {
    const total = chunks.reduce((sum, c) => sum + c.length, 0);
    const result = new Float32Array(total);
    let offset = 0;
    for (const c of chunks) {
      result.set(c, offset);
      offset += c.length;
    }
    return result;
  };

  return numChannels > 1 ? [concat(leftChunks), concat(rightChunks)] : [concat(leftChunks)];
}

// Scans a buffer's channels for the loudest sample, then scales every
// sample so that peak hits ~95% of full scale. Pitch-shifting can leave
// the output noticeably quieter than the original recording, so this
// brings the disguised voice back up to a consistent, clearly audible
// volume instead of trusting whatever level SoundTouchJS happens to
// produce.
function normalizePeak(channelsData: Float32Array[], target = 0.95): void {
  let peak = 0;
  for (const channel of channelsData) {
    for (let i = 0; i < channel.length; i++) {
      const abs = Math.abs(channel[i]);
      if (abs > peak) peak = abs;
    }
  }
  if (peak === 0) return; // silence — nothing to scale

  const gain = target / peak;
  if (gain <= 1) return; // already loud enough, don't attenuate

  for (const channel of channelsData) {
    for (let i = 0; i < channel.length; i++) {
      channel[i] *= gain;
    }
  }
}

// Generates a synthetic impulse response (exponentially-decaying noise)
// for algorithmic reverb — no external audio file needed. Used by
// Cave/Church/Sorcerer/Philosopher/etc.
function createImpulseResponse(ctx: OfflineAudioContext, seconds: number, decay: number): AudioBuffer {
  const length = Math.max(1, Math.floor(ctx.sampleRate * seconds));
  const impulse = ctx.createBuffer(2, length, ctx.sampleRate);
  for (let ch = 0; ch < 2; ch++) {
    const data = impulse.getChannelData(ch);
    for (let i = 0; i < length; i++) {
      data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / length, decay);
    }
  }
  return impulse;
}

// Simple saturation curve for a driven/distorted character (Megaphone,
// Devil's Advocate).
function makeDistortionCurve(amount: number): Float32Array {
  const samples = 1024;
  const curve = new Float32Array(samples);
  for (let i = 0; i < samples; i++) {
    const x = (i * 2) / samples - 1;
    curve[i] = ((3 + amount) * x * 20 * (Math.PI / 180)) / (Math.PI + amount * Math.abs(x));
  }
  return curve;
}

// Mixes a wet effect node in parallel with the dry signal via separate
// gain nodes, both feeding into `dest`. Returns the wet gain node's
// input so callers can connect their effect into it.
function addParallelMix(
  ctx: OfflineAudioContext,
  dryNode: AudioNode,
  dest: AudioNode,
  mix: number
): AudioNode {
  const dryGain = ctx.createGain();
  dryGain.gain.value = 1 - mix;
  dryNode.connect(dryGain);
  dryGain.connect(dest);

  const wetGain = ctx.createGain();
  wetGain.gain.value = mix;
  wetGain.connect(dest);
  return wetGain;
}

// Runs the pitch-shifted audio through the chosen preset's full effects
// chain: optional band-limiting, formant-correction EQ, reverb, echo,
// distortion, and vibrato, each mixed in only if the preset defines it.
// Ends with gentle compression so quiet syllables don't get buried.
async function enhanceClarity(buffer: AudioBuffer, preset: VoicePreset): Promise<AudioBuffer> {
  const offlineCtx = new OfflineAudioContext(
    buffer.numberOfChannels,
    buffer.length,
    buffer.sampleRate
  );

  const source = offlineCtx.createBufferSource();
  source.buffer = buffer;

  let lastNode: AudioNode = source;

  // Subtle low-end warmth on every preset. Real human voices carry
  // some body around 200Hz; without it, a pitch-shifted + EQ'd voice
  // reads as thin/synthetic rather than genuine.
  const warmth = offlineCtx.createBiquadFilter();
  warmth.type = "lowshelf";
  warmth.frequency.value = 200;
  warmth.gain.value = 1.5;
  lastNode.connect(warmth);
  lastNode = warmth;

  // Band-limiting (phone-call / megaphone character), applied first.
  if (preset.bandpass) {
    const highpass = offlineCtx.createBiquadFilter();
    highpass.type = "highpass";
    highpass.frequency.value = preset.bandpass.low;
    lastNode.connect(highpass);
    lastNode = highpass;

    const lowpass = offlineCtx.createBiquadFilter();
    lowpass.type = "lowpass";
    lowpass.frequency.value = preset.bandpass.high;
    lastNode.connect(lowpass);
    lastNode = lowpass;
  }

  // Formant-targeted peaking bands. Gain and Q are both pulled back
  // slightly from the preset's stated values — full-strength narrow,
  // high-gain peaks are what make an EQ'd voice sound resonant and
  // artificial rather than natural. A broader, gentler boost still
  // restores clarity without that "processed" edge.
  for (const band of preset.formantBands ?? []) {
    const peaking = offlineCtx.createBiquadFilter();
    peaking.type = "peaking";
    peaking.frequency.value = band.freq;
    peaking.gain.value = band.gain * 0.75;
    peaking.Q.value = Math.max(0.7, band.q * 0.8);
    lastNode.connect(peaking);
    lastNode = peaking;
  }

  // Distortion (driven waveshaper), wet/dry mixed.
  if (preset.distortion) {
    const shaper = offlineCtx.createWaveShaper();
    shaper.curve = makeDistortionCurve(preset.distortion.amount);
    shaper.oversample = "2x";
    const merge = offlineCtx.createGain();
    const wetIn = addParallelMix(offlineCtx, lastNode, merge, preset.distortion.mix);
    lastNode.connect(shaper);
    shaper.connect(wetIn);
    lastNode = merge;
  }

  // Vibrato: LFO-modulated micro-delay, wet/dry mixed.
  if (preset.vibrato) {
    const merge = offlineCtx.createGain();
    const delay = offlineCtx.createDelay(0.05);
    delay.delayTime.value = preset.vibrato.depthMs / 1000;
    const lfo = offlineCtx.createOscillator();
    lfo.frequency.value = preset.vibrato.rateHz;
    const lfoGain = offlineCtx.createGain();
    lfoGain.gain.value = (preset.vibrato.depthMs / 1000) * 0.9;
    lfo.connect(lfoGain);
    lfoGain.connect(delay.delayTime);
    lfo.start();

    const wetIn = addParallelMix(offlineCtx, lastNode, merge, preset.vibrato.mix);
    lastNode.connect(delay);
    delay.connect(wetIn);
    lastNode = merge;
  }

  // Echo: feedback delay, wet/dry mixed.
  if (preset.echo) {
    const merge = offlineCtx.createGain();
    const delay = offlineCtx.createDelay(1.5);
    delay.delayTime.value = preset.echo.delaySeconds;
    const feedback = offlineCtx.createGain();
    feedback.gain.value = preset.echo.feedback;
    delay.connect(feedback);
    feedback.connect(delay);

    const wetIn = addParallelMix(offlineCtx, lastNode, merge, preset.echo.mix);
    lastNode.connect(delay);
    delay.connect(wetIn);
    lastNode = merge;
  }

  // Reverb: algorithmic convolution, wet/dry mixed.
  if (preset.reverb) {
    const merge = offlineCtx.createGain();
    const convolver = offlineCtx.createConvolver();
    convolver.buffer = createImpulseResponse(offlineCtx, preset.reverb.seconds, preset.reverb.decay);

    const wetIn = addParallelMix(offlineCtx, lastNode, merge, preset.reverb.mix);
    lastNode.connect(convolver);
    convolver.connect(wetIn);
    lastNode = merge;
  }

  // Gentler than before (ratio 3→2.2, softer knee, slower attack) —
  // a heavier compressor "pumps"/flattens dynamics in a way that reads
  // as processed. This still keeps quiet syllables audible without
  // squashing the natural rise and fall of speech.
  const compressor = offlineCtx.createDynamicsCompressor();
  compressor.threshold.value = -22;
  compressor.knee.value = 24;
  compressor.ratio.value = 2.2;
  compressor.attack.value = 0.006;
  compressor.release.value = 0.28;

  lastNode.connect(compressor);
  compressor.connect(offlineCtx.destination);
  source.start();

  return offlineCtx.startRendering();
}

// Quantizes samples down to a reduced bit depth for a lo-fi/crackly
// character (Phonograph, Synth). Applied directly to sample data
// rather than the audio graph, since it's just per-sample rounding.
function applyBitcrush(channelsData: Float32Array[], bits: number): void {
  const levels = Math.pow(2, bits);
  for (const channel of channelsData) {
    for (let i = 0; i < channel.length; i++) {
      channel[i] = Math.round(channel[i] * levels) / levels;
    }
  }
}

// Encodes an AudioBuffer as a 16-bit PCM WAV Blob — no external library
// needed, WAV's format is simple enough to write by hand.
function audioBufferToWavBlob(buffer: AudioBuffer): Blob {
  const numChannels = buffer.numberOfChannels;
  const sampleRate = buffer.sampleRate;
  const numFrames = buffer.length;
  const bytesPerSample = 2;
  const blockAlign = numChannels * bytesPerSample;
  const dataSize = numFrames * blockAlign;
  const bufferSize = 44 + dataSize;

  const arrayBuffer = new ArrayBuffer(bufferSize);
  const view = new DataView(arrayBuffer);

  const writeString = (offset: number, str: string) => {
    for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i));
  };

  writeString(0, "RIFF");
  view.setUint32(4, bufferSize - 8, true);
  writeString(8, "WAVE");
  writeString(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * blockAlign, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bytesPerSample * 8, true);
  writeString(36, "data");
  view.setUint32(40, dataSize, true);

  const channels: Float32Array[] = [];
  for (let c = 0; c < numChannels; c++) channels.push(buffer.getChannelData(c));

  let offset = 44;
  for (let i = 0; i < numFrames; i++) {
    for (let c = 0; c < numChannels; c++) {
      const sample = Math.max(-1, Math.min(1, channels[c][i]));
      view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
      offset += 2;
    }
  }

  return new Blob([arrayBuffer], { type: "audio/wav" });
}

// Main entry point: takes a raw recorded Blob and returns a disguised
// WAV Blob. Picks one of several voice presets at random, shifts pitch
// down within that preset's range, runs it through that preset's
// formant-correction EQ, and normalizes to a consistent, clearly
// audible volume. Always shifts down, regardless of the speaker's
// original pitch. Optionally pass a preset name to force a specific
// one (useful for testing); otherwise one is chosen randomly each call.
export async function disguiseVoice(rawBlob: Blob, presetName?: string): Promise<Blob> {
  const arrayBuffer = await rawBlob.arrayBuffer();
  const audioCtx = new AudioContext();
  const decoded = await audioCtx.decodeAudioData(arrayBuffer);
  await audioCtx.close();

  const preset = presetName
    ? VOICE_PRESETS.find((p) => p.name === presetName) ?? pickRandomPreset()
    : pickRandomPreset();

  const [min, max] = preset.pitchRange;
  const shiftFactor = randomInRange(min, max);

  const soundTouch = new SoundTouch();
  soundTouch.pitch = shiftFactor;

  const source = new WebAudioBufferSource(decoded);
  const filter = new SimpleFilter(source, soundTouch);

  const channelsData = extractProcessedSamples(filter, decoded.numberOfChannels);

  const shiftedBuffer = new AudioBuffer({
    numberOfChannels: channelsData.length,
    length: channelsData[0].length,
    sampleRate: decoded.sampleRate,
  });
  channelsData.forEach((data, ch) => shiftedBuffer.copyToChannel(data, ch));

  const enhancedBuffer = await enhanceClarity(shiftedBuffer, preset);

  const finalChannels: Float32Array[] = [];
  for (let c = 0; c < enhancedBuffer.numberOfChannels; c++) {
    finalChannels.push(new Float32Array(enhancedBuffer.getChannelData(c)));
  }
  if (preset.bitcrushBits) applyBitcrush(finalChannels, preset.bitcrushBits);
  normalizePeak(finalChannels);

  const outBuffer = new AudioBuffer({
    numberOfChannels: finalChannels.length,
    length: finalChannels[0].length,
    sampleRate: enhancedBuffer.sampleRate,
  });
  finalChannels.forEach((data, ch) => outBuffer.copyToChannel(data, ch));

  return audioBufferToWavBlob(outBuffer);
}

// Used for the answerer's side, where we want their real voice preserved
// (no pitch shift) — we still just re-encode to WAV so playback/upload is
// consistent with the disguised path.
export async function encodeOwnVoice(rawBlob: Blob): Promise<Blob> {
  const arrayBuffer = await rawBlob.arrayBuffer();
  const audioCtx = new AudioContext();
  const decoded = await audioCtx.decodeAudioData(arrayBuffer);
  await audioCtx.close();

  return audioBufferToWavBlob(decoded);
}
